import type {
  ExportTemplateColumn,
  ExportTemplateDefinition,
  ExportTemplatePreview,
  ExportTransformation,
} from '../types/export.types';

/**
 * One column of one sampled product, paired stored-vs-exported, with everything
 * the preview views need to describe it. Built once per (response, product) so
 * the three views render the same facts rather than each re-deriving them.
 */
export interface PreviewCell {
  /** Connector wire name — the file's header for this column. */
  key: string;
  label: string;
  /** Identifier columns are the ones a feed cannot be ingested without. */
  identifier: boolean;
  raw: string;
  encoded: string;
  changed: boolean;
  /** Transformation code the template names for this column, if any. */
  transformationCode: string | null;
  transformationLabel: string | null;
  /** The encoding's own one-line explanation, from the service's config. Shown
   *  ON the change rather than only in the Encodings tab: "what happened here"
   *  and "why" are the same question, and splitting them across two tabs is
   *  what made the pair read as duplicates. */
  transformationDescription: string | null;
  /** Vendor limit this exported value exceeds, when the spec defines one and
   *  the value is over it. `null` when within limits or when no spec exists. */
  specWarning: SpecWarning | null;
}

/**
 * A vendor limit the exported value exceeds. Advisory only — the export neither
 * truncates nor rejects, so this warns rather than blocks. Naming the limit and
 * the actual size is the point: it turns "GMC rejected my feed" into something
 * the operator can see before uploading.
 */
export interface SpecWarning {
  kind: 'length' | 'repetitions';
  /** Measured size of the exported value (characters, or comma-joined items). */
  actual: number;
  /** The vendor's published maximum. */
  limit: number;
  /** Vendor documentation for the column, when the config carries one. */
  docUrl: string | null;
}

/** The feed encodings join repeated values with commas, so a repetition count
 *  is the number of comma-separated items in the exported cell. */
const REPETITION_SEPARATOR = ',';

/**
 * Measures one exported value against its column's published spec and returns
 * the first limit it breaks, or `null`. Length is checked before repetitions
 * because an over-long cell is the more common and more visible failure.
 */
export function specWarningFor(
  encoded: string,
  column: ExportTemplateColumn | undefined,
): SpecWarning | null {
  const spec = column?.spec;
  if (!spec || encoded === '') return null;
  const docUrl = spec.docUrl ?? null;

  if (typeof spec.maxLength === 'number' && encoded.length > spec.maxLength) {
    return { kind: 'length', actual: encoded.length, limit: spec.maxLength, docUrl };
  }

  if (typeof spec.maxRepetitions === 'number') {
    const count = encoded.split(REPETITION_SEPARATOR).length;
    if (count > spec.maxRepetitions) {
      return { kind: 'repetitions', actual: count, limit: spec.maxRepetitions, docUrl };
    }
  }

  return null;
}

/**
 * Flattens one sampled product of a preview response into per-column cells.
 *
 * `header` is the authority on column order and count — `raw`/`encoded` are
 * parallel arrays of the same length, so a missing entry reads as an empty cell
 * rather than shifting every column after it.
 */
export function buildPreviewCells({
  preview,
  rowIndex,
  template,
  columnsByKey,
  transformationsByCode,
}: {
  preview: ExportTemplatePreview | undefined;
  rowIndex: number;
  template: ExportTemplateDefinition | undefined;
  columnsByKey: Record<string, ExportTemplateColumn>;
  transformationsByCode: Record<string, ExportTransformation>;
}): PreviewCell[] {
  const row = preview?.rows[rowIndex];
  if (!preview || !row) return [];

  return preview.header.map((key, position) => {
    const raw = row.raw[position] ?? '';
    const encoded = row.encoded[position] ?? '';
    const column = columnsByKey[key];
    const code = template?.transformations[key] ?? null;
    return {
      key,
      label: column?.label ?? key,
      // The template's own list when published: it names identifiers the
      // connector does not declare (`gmc-feed`'s `offer_id`).
      identifier: template?.identifiers
        ? template.identifiers.includes(key)
        : (column?.identifier ?? false),
      raw,
      encoded,
      changed: raw !== encoded,
      transformationCode: code,
      transformationLabel: code ? (transformationsByCode[code]?.label ?? code) : null,
      transformationDescription: code ? (transformationsByCode[code]?.description ?? null) : null,
      specWarning: specWarningFor(encoded, column),
    };
  });
}

/** One encoding used by the active template, with the columns it applies to.
 *  Drives the Encodings view, built entirely from config already in the cache —
 *  `before`/`after` are the service's own worked examples. */
export interface AppliedTransformation extends ExportTransformation {
  /** Labels of the columns this template encodes with it, in header order. */
  columnLabels: string[];
}

/**
 * The distinct transformations the active template applies, each with the
 * columns using it. Ordered by column count descending so the encoding that
 * touches most of the file is explained first. Empty for a raw template, whose
 * `transformations` delta is `{}`.
 */
export function appliedTransformations({
  template,
  columnsByKey,
  transformationsByCode,
  header,
}: {
  template: ExportTemplateDefinition | undefined;
  columnsByKey: Record<string, ExportTemplateColumn>;
  transformationsByCode: Record<string, ExportTransformation>;
  /** Restricts and orders the result to the columns actually being exported. */
  header: string[] | undefined;
}): AppliedTransformation[] {
  if (!template) return [];
  const keys = header ?? Object.keys(template.transformations);
  const byCode = new Map<string, AppliedTransformation>();

  for (const key of keys) {
    const code = template.transformations[key];
    if (code === undefined) continue;
    const definition = transformationsByCode[code];
    if (!definition) continue;
    const label = columnsByKey[key]?.label ?? key;
    const existing = byCode.get(code);
    if (existing) existing.columnLabels.push(label);
    else byCode.set(code, { ...definition, columnLabels: [label] });
  }

  return [...byCode.values()].sort((a, b) => b.columnLabels.length - a.columnLabels.length);
}

/**
 * Serialises the sampled products as the delimited text the export would
 * contain, for the copy/download affordance. Uses the chosen format's real
 * delimiter, so what the operator pastes into a spreadsheet matches the file.
 *
 * CSV/TXT use RFC-4180 quoting: a field is quoted when it contains the
 * delimiter, a quote, or a line break, and inner quotes are doubled. Without it
 * a sample of comma-joined feed values would re-parse into the wrong columns —
 * exactly the collision the TSV recommendation warns about.
 *
 * TSV is written verbatim, matching export-service's row writer: Google reads a
 * TSV value literally, so quoting an already-encoded `product_highlight` or
 * `product_detail` cell again made it reject the file. A tab or line break in a
 * value becomes a space, since a TSV cell cannot carry one.
 */
export function previewToDelimitedText({
  preview,
  delimiter,
}: {
  preview: ExportTemplatePreview;
  delimiter: string;
}): string {
  const quote = (value: string): string => {
    if (delimiter === '\t') return value.replaceAll(/\r\n|[\t\r\n]/g, ' ');
    return value.includes(delimiter) ||
      value.includes('"') ||
      value.includes('\n') ||
      value.includes('\r')
      ? `"${value.replaceAll('"', '""')}"`
      : value;
  };

  const lines = [preview.header.map(quote).join(delimiter)];
  for (const row of preview.rows) {
    lines.push(preview.header.map((_, i) => quote(row.encoded[i] ?? '')).join(delimiter));
  }
  return lines.join('\n');
}
